import { Button, InputGroup, Spinner, SpinnerSize } from "@blueprintjs/core";
import { FormikProps } from "formik";
import {
  FGCustomInput,
  FGSelectInput,
  FGTextInput,
  FieldGroup,
  FGMultiCheckboxInput,
  FGCheckboxInput,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import { PasswordApi, UserDto, UsersApi } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { SmallFormGenerator } from "../../../../components";
import { useAuth, useEventsContext } from "../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";

export interface IUserDetailPageProps {
  userId: number;
  editMode: boolean;
}

export const UserDetailPage: React.FunctionComponent<IUserDetailPageProps> = ({ userId, editMode }) => {
  const { t } = useTl();
  const api = useApiService(UsersApi);
  const passwordApi = useApiService(PasswordApi);
  const history = useHistory();
  const { hasAction } = useAuth();

  const [profile, pLoading] = useReferential((a) => a.getUserProfiles(), true);
  const [language, lLoading] = useReferential((a) => a.getLanguages(), true);
  const [department, dLoading] = useReferential((a) => a.getDepartments(), true);
  const [password, setPassword] = React.useState("");

  const getUser = React.useCallback(async () => {
    const user = await api.get(userId);
    return UserDto.fromJS({ ...user, password: password });
  }, [api, password, userId]);

  const { data, loading, deleteItem, deleting, saveItem, saving, refresh } = useCrudApi<UserDto>(
    React.useMemo(
      () => ({
        getApiFn: async (id = userId) => (id > 0 ? await getUser() : new UserDto({ active: true }).toJSON()),
        saveApiFn: (d) => api.save(d),
        onSavedRoute: (d) => `${ERoutes.user}/${d.userId}/detail/edit`,
        deleteApiFn: (d) => api.delete(d.userId),
        onDeletedRoute: () => ERoutes.user,
      }),
      [api, getUser, userId],
    ),
    [userId],
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      lastName: Yup.string().nullable().required(t(ETLCodes.Required)),
      firstName: Yup.string().nullable().required(t(ETLCodes.Required)),
      email: Yup.string().nullable().required(t(ETLCodes.Required)),
      userProfileId: Yup.number().nullable().required(t(ETLCodes.Required)),
      languageId: Yup.number().nullable().required(t(ETLCodes.Required)),
    });
  }, [t]);

  const getNewPassword = React.useCallback(
    async (formik: FormikProps<UserDto>) => {
      const pass = await passwordApi.getNewPassword();
      setPassword(pass);
      formik.setFieldValue("password", pass);
    },
    [passwordApi],
  );

  const OnLanguageChangedEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("LanguageChanged", OnLanguageChangedEvent);
    return () => unsubscribeEvent("LanguageChanged", OnLanguageChangedEvent);
  }, [subscribeToEvent, unsubscribeEvent, OnLanguageChangedEvent]);

  return (
    data && (
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        validationSchema={FormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.user)}
        onDelete={deleteItem}
        showDeleteButton={userId > 0}
        saving={saving}
        deleting={deleting}
      >
        <FieldGroup columns={1}>
          <FGTextInput name="lastName" label={t(ETLCodes.LastName)} />
          <FGTextInput name="firstName" label={t(ETLCodes.FirstName)} />

          {userId > 0 && (
            <FGCustomInput name="active" label={t(ETLCodes.General_Password)}>
              {(ctx) => (
                <InputGroup
                  readOnly={true}
                  value={ctx.formik.values?.password}
                  rightElement={
                    loading ? (
                      <Spinner size={SpinnerSize.SMALL} />
                    ) : (
                      <Button
                        text={t(ETLCodes.ResetPassword)}
                        rightIcon={"reset"}
                        intent={"primary"}
                        minimal={true}
                        onClick={() => getNewPassword(ctx.formik)}
                      />
                    )
                  }
                />
              )}
            </FGCustomInput>
          )}

          <FGTextInput name="email" label={t(ETLCodes.Email)} />
          <FieldGroup columns={2}>
            <FGSelectInput name="languageId" label={t(ETLCodes.Language)} items={language} loading={lLoading} />
            <FGSelectInput
              name="userProfileId"
              label={t(ETLCodes.Profile)}
              items={profile}
              loading={pLoading}
              readonly={!hasAction("UPDATEUSERPERMISSION")}
            />
          </FieldGroup>
          <FGCheckboxInput name="active" label={t(ETLCodes.Active)} />
          <FGMultiCheckboxInput
            name="departments"
            label={t(ETLCodes.Departments)}
            items={department}
            loading={dLoading}
          />
        </FieldGroup>
      </SmallFormGenerator>
    )
  );
};
